import { getMappedColors } from '../colorsMap'
import { getAxisLabelFormatter } from '../formatChartData'
import { ChartData } from '@/Atomic/Organisms/Charts/acBar/dataConverter'
import { getCurrentLocale } from '@/utils/localeMapping'

export function getGaugeOptionsAndDataV2(data: ChartData) {
  try {
    const { metaData, chartData = [] } = data

    // Extract colors
    const colors = getMappedColors(metaData.colors || [])

    const locale = getCurrentLocale()
    const formatValue = getAxisLabelFormatter(
      metaData.properties?.yFormat,
      locale,
      (value: number) => value.toLocaleString(locale),
    )

    // Extract needle value, min, and max first
    const needleData = chartData.find((item) => item.type === 'needle')
    const minData = chartData.find((item) => item.type === 'min')
    const maxData = chartData.find((item) => item.type === 'max')

    if (!needleData) {
      throw new Error('Needle data not found in chartData')
    }

    const value = needleData.value
    const minValue = minData?.value ?? 0
    let maxValue = maxData?.value ?? value

    // Extract all intervals including zero values for color mapping
    const allIntervals = chartData.filter((item) => item.type === 'interval')

    // Calculate cumulative limits, starting from minValue
    let cumulativeLimit = minValue
    const allSubArcs = allIntervals.map((item) => {
      cumulativeLimit += item.value
      return {
        limit: cumulativeLimit,
        color: colors[item.color ?? 0] || '#000',
        showTick: true,
        value: item.value,
      }
    })

    // Remove arcs that don't increase the limit (strictly increasing requirement)
    let subArcs = allSubArcs.filter((arc, index) => {
      if (index === 0) return arc.limit > minValue
      const prevLimit = allSubArcs[index - 1]?.limit ?? minValue
      return arc.limit > prevLimit
    })

    // Handle edge cases for zero or missing intervals
    if (allIntervals.length === 0) {
      // No interval data - use safe defaults with no colored segments
      if (maxValue <= minValue) {
        // Use a default range of 100 to prevent division by zero in gauge rendering
        maxValue = minValue + 100
      }
      subArcs = []
    } else if (subArcs.length === 0) {
      // All intervals are zero, create equal-sized colored segments using group values
      // Use group values if available, otherwise fallback to sequential numbering
      const hasGroupValues = allIntervals.every(
        (item) => item.group !== undefined,
      )

      if (hasGroupValues) {
        // Sort intervals by group value to ensure correct cumulative limits
        const sortedIntervals = [...allIntervals].sort(
          (a, b) => (a.group ?? 0) - (b.group ?? 0),
        )
        subArcs = sortedIntervals.map((item) => ({
          limit: item.group ?? 0,
          color: colors[item.color ?? 0] || '#000',
          showTick: true,
          value: 1,
        }))
        // Set maxValue to the last arc's limit
        maxValue =
          sortedIntervals[sortedIntervals.length - 1]?.group ??
          allIntervals.length
      } else {
        maxValue = allIntervals.length
        subArcs = allIntervals.map((item, index) => ({
          limit: index + 1,
          color: colors[item.color ?? 0] || '#000',
          showTick: true,
          value: 1,
        }))
      }
    } else if (maxValue <= minValue) {
      // Use a default range of 100 to prevent division by zero in gauge rendering
      maxValue = minValue + 100
    }

    // Extract tick labels
    const tickLabels = subArcs.map((arc) => ({ value: arc.limit }))

    // Final React Gauge Component Configuration
    const gaugeConfig = {
      type: 'radial',
      arc: {
        width: 0.2,
        padding: 0.005,
        cornerRadius: 13,
        subArcs: subArcs,
      },
      pointer: { type: 'arrow', color: '#dfa810' },
      labels: {
        valueLabel: {
          style: { fontSize: 50, fill: '#dfa810', textShadow: 'none' },
          formatTextValue: formatValue,
        },
        tickLabels: {
          type: 'outer',
          defaultTickValueConfig: {
            formatTextValue: formatValue,
            style: { fontSize: 10, fill: 'gray' },
          },
          ticks: tickLabels,
        },
      },
      value: value,
      minValue: minValue,
      maxValue: maxValue,
    }

    return gaugeConfig
  } catch (error) {
    console.error(
      'Error processing gauge chart data:',
      error instanceof Error ? error.message : 'Unknown error',
    )

    // Return safe fallback configuration
    return {
      type: 'radial',
      arc: {
        width: 0.2,
        padding: 0.005,
        cornerRadius: 13,
        subArcs: [],
      },
      pointer: { type: 'arrow', color: '#dfa810' },
      labels: {
        valueLabel: {
          style: { fontSize: 50, fill: '#dfa810', textShadow: 'none' },
          formatTextValue: (value: number) => `${value}`,
        },
        tickLabels: {
          type: 'outer',
          defaultTickValueConfig: {
            formatTextValue: (value: number) => `${value}`,
            style: { fontSize: 10, fill: 'gray' },
          },
          ticks: [],
        },
      },
      value: 0,
      minValue: 0,
      maxValue: 100,
    }
  }
}
